import { useState } from "react";
import DragNumberInput from "./DragNumberInput";

function attachPointerRaise(handleKey, setActiveHandle, { freezeTrack, unfreezeTrack } = {}) {
  return {
    onPointerDown: (e) => {
      freezeTrack?.();
      setActiveHandle(handleKey);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    onPointerUp: (e) => {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      setActiveHandle(null);
      unfreezeTrack?.();
    },
    onPointerCancel: (e) => {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      setActiveHandle(null);
      unfreezeTrack?.();
    },
  };
}

function parseInputNumber(raw) {
  const t = String(raw).trim().replace(",", ".");
  if (t === "" || t === "-" || t === "." || t === "-.") return null;
  const v = Number(t);
  return Number.isFinite(v) ? v : null;
}

const PANEL_NUMBER_INPUT_CLASS =
  "w-full min-w-0 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm";

/** Horizontal px needed to drag across full `minBound`…`maxBound` span (label drag — slow / precise). */
const LABEL_DRAG_PIXELS_PER_FULL_SPAN = 4200;

/**
 * Three thumbs on one track with enforced ordering: low ≤ mid ≤ high (cannot cross).
 * While a native range thumb is pressed, the track min/max (from props) are held fixed until
 * pointer-up so padding around limits does not redraw mid-drag; typing in the fields keeps props live.
 */
const TripleOrderedRangeSlider = ({
  minBound,
  maxBound,
  limitStep,
  valueStep,
  valueLow,
  valueMid,
  valueHigh,
  onLowChange,
  onMidChange,
  onHighChange,
  lowEnabled = true,
  highEnabled = true,
  disabled = false,
  lowAriaLabel = "Limit minimum",
  midAriaLabel = "Current value",
  highAriaLabel = "Limit maximum",
  showNumberInputs = true,
  className = "",
}) => {
  const [activeHandle, setActiveHandle] = useState(null);
  /** While a range thumb is held, keep min/max track extent fixed so limits ± padding do not live-update from the drag. */
  const [trackBoundsFrozen, setTrackBoundsFrozen] = useState(null);

  const trackMin = trackBoundsFrozen?.minBound ?? minBound;
  const trackMax = trackBoundsFrozen?.maxBound ?? maxBound;
  const span = trackMax - trackMin || 1;
  const labelDragSensitivity = span / LABEL_DRAG_PIXELS_PER_FULL_SPAN;
  const loLim = Number(valueLow);
  const hiLim = Number(valueHigh);
  const midValue = Number(valueMid);
  const fillLow = lowEnabled && Number.isFinite(loLim) ? loLim : midValue;
  const fillHigh = highEnabled && Number.isFinite(hiLim) ? hiLim : midValue;
  const leftPct = ((Math.min(fillLow, fillHigh) - trackMin) / span) * 100;
  const widthPct = (Math.abs(fillHigh - fillLow) / span) * 100;

  const freezeTrack = () => {
    setTrackBoundsFrozen((prev) => prev ?? { minBound, maxBound });
  };
  const unfreezeTrack = () => setTrackBoundsFrozen(null);

  const emitLow = (raw, { fromSlider = false } = {}) => {
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    const mid = Number(valueMid);
    let capped = v;
    if (fromSlider) {
      capped = Number.isFinite(mid) ? Math.min(Math.max(v, trackMin), mid) : Math.max(v, trackMin);
    } else if (Number.isFinite(mid)) {
      capped = Math.min(v, mid);
    }
    onLowChange(capped);
  };

  const emitMid = (raw, { fromSlider = false } = {}) => {
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    const lo = lowEnabled ? Number(valueLow) : NaN;
    const hi = highEnabled ? Number(valueHigh) : NaN;
    let capped = v;
    if (Number.isFinite(lo)) capped = Math.max(capped, lo);
    if (Number.isFinite(hi)) capped = Math.min(capped, hi);
    if (fromSlider) {
      capped = Math.min(Math.max(capped, trackMin), trackMax);
    }
    onMidChange(capped);
  };

  const emitHigh = (raw, { fromSlider = false } = {}) => {
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    const mid = Number(valueMid);
    let capped = v;
    if (fromSlider) {
      capped = Number.isFinite(mid) ? Math.max(Math.min(v, trackMax), mid) : Math.min(v, trackMax);
    } else if (Number.isFinite(mid)) {
      capped = Math.max(v, mid);
    }
    onHighChange(capped);
  };

  const zLow =
    activeHandle === "low" ? 45 : activeHandle === "mid" ? 14 : activeHandle === "high" ? 12 : 18;
  const zMid =
    activeHandle === "mid" ? 45 : activeHandle === "low" ? 22 : activeHandle === "high" ? 22 : 38;
  const zHigh =
    activeHandle === "high" ? 45 : activeHandle === "low" ? 12 : activeHandle === "mid" ? 26 : 30;

  const ptrOpts = { freezeTrack, unfreezeTrack };
  const ptrLow = attachPointerRaise("low", setActiveHandle, ptrOpts);
  const ptrMid = attachPointerRaise("mid", setActiveHandle, ptrOpts);
  const ptrHigh = attachPointerRaise("high", setActiveHandle, ptrOpts);

  const lo = Number(valueLow);
  const hi = Number(valueHigh);
  const mid = Number(valueMid);

  return (
    <div className={`triple-ordered-range w-full flex flex-col gap-2 ${className}`.trim()}>
      <div className="flex flex-col gap-0.5">
        <div className="grid grid-cols-3 gap-2 text-xs text-gray-400 select-none" aria-hidden>
          <span className="text-left">{lowEnabled ? "Min" : ""}</span>
          <span className="text-center font-medium text-gray-300">Current</span>
          <span className="text-right">{highEnabled ? "Max" : ""}</span>
        </div>
        <div className="dual-range-slider relative h-11 w-full shrink-0">
          <div className="dual-range-slider-track-bg pointer-events-none" aria-hidden />
          <div
            className="dual-range-slider-fill pointer-events-none"
            style={{
              left: `${leftPct}%`,
              width: `${Math.max(widthPct, 0)}%`,
            }}
            aria-hidden
          />
          {lowEnabled ? (
            <input
              type="range"
              className="dual-range-slider-input absolute inset-x-0 top-1/2 w-full -translate-y-1/2"
              style={{ zIndex: zLow }}
              min={trackMin}
              max={trackMax}
              step={limitStep}
              value={Number(valueLow)}
              disabled={disabled}
              aria-label={lowAriaLabel}
              aria-valuemin={trackMin}
              aria-valuemax={trackMax}
              aria-valuenow={Number(valueLow)}
              onChange={(e) => emitLow(e.target.value, { fromSlider: true })}
              {...ptrLow}
            />
          ) : null}
          <input
            type="range"
            className="dual-range-slider-input dual-range-slider-input-current absolute inset-x-0 top-1/2 w-full -translate-y-1/2"
            style={{ zIndex: zMid }}
            min={trackMin}
            max={trackMax}
            step={valueStep}
            value={Number(valueMid)}
            disabled={disabled}
            aria-label={midAriaLabel}
            aria-valuemin={trackMin}
            aria-valuemax={trackMax}
            aria-valuenow={Number(valueMid)}
            onChange={(e) => emitMid(e.target.value, { fromSlider: true })}
            {...ptrMid}
          />
          {highEnabled ? (
            <input
              type="range"
              className="dual-range-slider-input absolute inset-x-0 top-1/2 w-full -translate-y-1/2"
              style={{ zIndex: zHigh }}
              min={trackMin}
              max={trackMax}
              step={limitStep}
              value={Number(valueHigh)}
              disabled={disabled}
              aria-label={highAriaLabel}
              aria-valuemin={trackMin}
              aria-valuemax={trackMax}
              aria-valuenow={Number(valueHigh)}
              onChange={(e) => emitHigh(e.target.value, { fromSlider: true })}
              {...ptrHigh}
            />
          ) : null}
        </div>
      </div>
      {showNumberInputs ? (
        <div className="grid grid-cols-3 gap-2">
          {lowEnabled ? (
            <DragNumberInput
              label="Min"
              labelClassName="text-xs text-gray-300"
              value={lo}
              dragValue={lo}
              disabled={disabled}
              deferDragUpdates={false}
              dragSensitivity={labelDragSensitivity}
              dragSnapStep={limitStep}
              onDragValueChange={(v) => emitLow(v)}
              inputProps={{ "aria-label": lowAriaLabel }}
              onChange={(e) => {
                const v = parseInputNumber(e.target.value);
                if (v == null) return;
                emitLow(v);
              }}
              inputClassName={PANEL_NUMBER_INPUT_CLASS}
              wrapperClassName="min-w-0 space-y-1"
            />
          ) : (
            <div aria-hidden />
          )}
          <DragNumberInput
            label="Current"
            labelClassName="text-xs text-gray-300"
            value={mid}
            dragValue={mid}
            disabled={disabled}
            deferDragUpdates={false}
            dragSensitivity={labelDragSensitivity}
            dragSnapStep={valueStep}
            onDragValueChange={(v) => emitMid(v)}
            inputProps={{ "aria-label": midAriaLabel }}
            onChange={(e) => {
              const v = parseInputNumber(e.target.value);
              if (v == null) return;
              emitMid(v);
            }}
            inputClassName={PANEL_NUMBER_INPUT_CLASS}
            wrapperClassName="min-w-0 space-y-1"
          />
          {highEnabled ? (
            <DragNumberInput
              label="Max"
              labelClassName="text-xs text-gray-300"
              value={hi}
              dragValue={hi}
              disabled={disabled}
              deferDragUpdates={false}
              dragSensitivity={labelDragSensitivity}
              dragSnapStep={limitStep}
              onDragValueChange={(v) => emitHigh(v)}
              inputProps={{ "aria-label": highAriaLabel }}
              onChange={(e) => {
                const v = parseInputNumber(e.target.value);
                if (v == null) return;
                emitHigh(v);
              }}
              inputClassName={PANEL_NUMBER_INPUT_CLASS}
              wrapperClassName="min-w-0 space-y-1"
            />
          ) : (
            <div aria-hidden />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default TripleOrderedRangeSlider;
